// Font files for embedding: WOFF (what the font CDNs serve) back to a plain TrueType font, and a glyph subset that keeps
// glyph ids unchanged, so a PDF can use CIDToGIDMap /Identity and carry only the characters a page uses.

import { inflate } from "./inflate";

const u16 = (b: Uint8Array, o: number) => (b[o]! << 8) | b[o + 1]!;
const u32 = (b: Uint8Array, o: number) => ((b[o]! << 24) | (b[o + 1]! << 16) | (b[o + 2]! << 8) | b[o + 3]!) >>> 0;
const tagAt = (b: Uint8Array, o: number) => String.fromCharCode(b[o]!, b[o + 1]!, b[o + 2]!, b[o + 3]!);

function put32(b: Uint8Array, o: number, v: number) { b[o] = (v >>> 24) & 255; b[o + 1] = (v >>> 16) & 255; b[o + 2] = (v >>> 8) & 255; b[o + 3] = v & 255; }
function put16(b: Uint8Array, o: number, v: number) { b[o] = (v >>> 8) & 255; b[o + 1] = v & 255; }

/** Table sum of a (padded) table, as the sfnt directory wants it. */
function checksum(b: Uint8Array): number {
    let sum = 0;
    for (let i = 0; i < b.length; i += 4) sum = (sum + (((b[i] ?? 0) << 24) | ((b[i + 1] ?? 0) << 16) | ((b[i + 2] ?? 0) << 8) | (b[i + 3] ?? 0))) >>> 0;
    return sum;
}

/** Assembles an sfnt from its tables. */
export function buildSfnt(flavor: number, tables: Map<string, Uint8Array>): Uint8Array {
    const tags = [...tables.keys()].sort();
    const n = tags.length;
    let log = 0;
    while (1 << (log + 1) <= n) log++;
    const searchRange = 16 * (1 << log);
    let size = 12 + 16 * n;
    const offsets: number[] = [];
    for (const t of tags) { offsets.push(size); size += (tables.get(t)!.length + 3) & ~3; }
    const out = new Uint8Array(size);
    put32(out, 0, flavor);
    put16(out, 4, n);
    put16(out, 6, searchRange);
    put16(out, 8, log);
    put16(out, 10, n * 16 - searchRange);
    tags.forEach((t, i) => {
        const d = tables.get(t)!;
        const e = 12 + i * 16;
        for (let k = 0; k < 4; k++) out[e + k] = t.charCodeAt(k);
        out.set(d, offsets[i]!);
        put32(out, e + 4, checksum(out.subarray(offsets[i]!, offsets[i]! + ((d.length + 3) & ~3))));
        put32(out, e + 8, offsets[i]!);
        put32(out, e + 12, d.length);
    });
    return out;
}

/** The tables of an sfnt (TrueType or OpenType) by tag. */
export function readTables(b: Uint8Array): { flavor: number; tables: Map<string, Uint8Array> } {
    const n = u16(b, 4);
    const tables = new Map<string, Uint8Array>();
    for (let i = 0; i < n; i++) {
        const e = 12 + i * 16;
        const off = u32(b, e + 8), len = u32(b, e + 12);
        if (off + len <= b.length) tables.set(tagAt(b, e), b.subarray(off, off + len));
    }
    return { flavor: u32(b, 0), tables };
}

/** Unwraps a WOFF 1.0 font into the sfnt it holds. */
export function woffToSfnt(w: Uint8Array): Uint8Array {
    if (tagAt(w, 0) !== "wOFF") throw new Error("Not a WOFF font.");
    const flavor = u32(w, 4), n = u16(w, 12);
    const tables = new Map<string, Uint8Array>();
    for (let i = 0; i < n; i++) {
        const e = 44 + i * 20;
        const off = u32(w, e + 4), comp = u32(w, e + 8), orig = u32(w, e + 12);
        const src = w.subarray(off, off + comp);
        tables.set(tagAt(w, e), comp < orig ? inflate(src) : src.slice());
    }
    return buildSfnt(flavor, tables);
}

/**
 * Keeps the outlines of `keep` (and the glyphs a composite uses) and empties every other glyph, leaving all glyph ids where they
 * are. Returns null for a font without TrueType outlines (an OpenType CFF font).
 */
export function subsetTrueType(sfnt: Uint8Array, keep: Iterable<number>): Uint8Array | null {
    const { flavor, tables } = readTables(sfnt);
    const head = tables.get("head"), loca = tables.get("loca"), glyf = tables.get("glyf"), maxp = tables.get("maxp"), hhea = tables.get("hhea"), hmtx = tables.get("hmtx");
    if (!head || !loca || !glyf || !maxp || !hhea || !hmtx) return null;
    const numGlyphs = u16(maxp, 4);
    const long = u16(head, 50) !== 0;
    const at = (g: number) => (long ? u32(loca, g * 4) : u16(loca, g * 2) * 2);
    const slice = (g: number) => (g < numGlyphs ? glyf.subarray(at(g), at(g + 1)) : new Uint8Array(0));
    const wanted = new Set<number>([0]);
    const stack = [...keep];
    while (stack.length) {
        const g = stack.pop()!;
        if (g < 0 || g >= numGlyphs || wanted.has(g)) continue;
        wanted.add(g);
        const d = slice(g);
        if (d.length >= 10 && (u16(d, 0) & 0x8000) !== 0) {
            let p = 10;
            for (;;) {
                if (p + 4 > d.length) break;
                const flags = u16(d, p), gi = u16(d, p + 2);
                stack.push(gi);
                p += 4 + (flags & 1 ? 4 : 2);
                if (flags & 8) p += 2; else if (flags & 0x40) p += 4; else if (flags & 0x80) p += 8;
                if (!(flags & 0x20)) break;
            }
        }
    }
    let total = 0;
    for (const g of wanted) total += (slice(g).length + 3) & ~3;
    const newGlyf = new Uint8Array(total);
    const newLoca = new Uint8Array((numGlyphs + 1) * 4);
    let pos = 0;
    for (let g = 0; g < numGlyphs; g++) {
        put32(newLoca, g * 4, pos);
        if (wanted.has(g)) { const d = slice(g); newGlyf.set(d, pos); pos += (d.length + 3) & ~3; }
    }
    put32(newLoca, numGlyphs * 4, pos);
    const newHead = head.slice();
    put32(newHead, 8, 0);
    put16(newHead, 50, 1);
    const out = new Map<string, Uint8Array>([["head", newHead], ["hhea", hhea], ["maxp", maxp], ["hmtx", hmtx], ["loca", newLoca], ["glyf", newGlyf]]);
    for (const t of ["cvt ", "fpgm", "prep"]) { const d = tables.get(t); if (d) out.set(t, d); }
    return buildSfnt(flavor, out);
}
